import { getEcState, useCookies } from '@packages/utilities';
import { getMainAudience } from '../../utils/audiences';
import { Audiences } from '../../../interfaces/components';
import { useFilterTimeControl } from '../../utils/filterTimeControl';
import { SnippetTypeList } from '../../../interfaces/contentSnippet';
import { BannerItem } from './BannerItem';

import type { MbaBannerType } from '../../../interfaces/contentSnippet';
import type { CmsDataJsonApi } from '../../../interfaces/jsonapi';
import type { CmsImageType } from '../../components/CmsImage/types';

export type BannerProps = {
  /** Fetched initial JSON data used to render the banner */
  bannerData?: CmsDataJsonApi;
};

/**
 * Displays linked banners on MBA pages.
 *
 * @param {Object} bannerData - Data object containing banner details
 * @returns {JSX.Element|null} Rendered banners or null if no banners are present
 */
export const Banner = ({ bannerData }: BannerProps) => {
  const filterTimeControl = useFilterTimeControl();
  const { getCookies } = useCookies();

  // The Audiences Enum lists the customer audiences with priority from high (lower index) to low (higher index)
  // Use it as array and cut away the audiences with higher priority (lower index) than the given target audience
  const audienceArray = Object.values(Audiences) as Audiences[keyof Audiences][];
  const audience = getMainAudience(getEcState(getCookies()));
  const audiencesLeft = audienceArray.slice(
    audienceArray.findIndex((audienceFromList) => audienceFromList === audience),
  );

  const filteredItems = bannerData?.data
    ?.filter(
      (item) =>
        item.type === SnippetTypeList.contentBannerMba &&
        item.field_paragraph?.type !== 'paragraph--minibanner' &&
        filterTimeControl(item),
    )
    ?.filter((item) => {
      if (item.type !== SnippetTypeList.contentBannerMba || !item.field_user_status) {
        return false;
      }
      return (
        // if there is no user status set for this banner -> any audience fits
        ('data' in item.field_user_status && item.field_user_status.data === null) ||
        // if there is a user status set for this banner -> check it against the current user audience hierarchy
        (item.field_user_status.name && audiencesLeft.includes(item.field_user_status.name))
      );
    })
    ?.sort((a, b) => {
      if (
        a.type !== SnippetTypeList.contentBannerMba ||
        !('field_user_status' in a) ||
        b.type !== SnippetTypeList.contentBannerMba ||
        !('field_user_status' in b)
      ) {
        return 0;
      }
      if (
        a.field_user_status &&
        'data' in a.field_user_status &&
        a.field_user_status.data === null
      ) {
        return 1;
      }
      if (
        b.field_user_status &&
        'data' in b.field_user_status &&
        b.field_user_status.data === null
      ) {
        return -1;
      }
      // after selecting all possible banners - sort the current banner data array by the audience priorities
      const aIndex = audienceArray.findIndex(
        (audienceName) =>
          a.field_user_status &&
          'name' in a.field_user_status &&
          audienceName === a.field_user_status.name,
      );
      const bIndex = audienceArray.findIndex(
        (audienceName) =>
          b.field_user_status &&
          'name' in b.field_user_status &&
          audienceName === b.field_user_status.name,
      );
      return aIndex - bIndex;
    });

  return filteredItems && filteredItems.length > 0
    ? filteredItems.map((item, index) => (
        <BannerItem
          // eslint-disable-next-line react/no-array-index-key
          key={index}
          data={(item as MbaBannerType).field_paragraph as CmsImageType}
        />
      ))
    : null;
};
